import React from "react";
import { Authentication } from "@hex-insights/app-modules";
import { Column, Conditional, Else, Heading, If, Image, Paragraph, Row } from "@hex-insights/core";
import { Form, PasswordField, SubmitButton } from "@hex-insights/forms";
import { InternalLink, Redirect, useActivePageRegistration, useHistory } from "@hex-insights/router";
import { serverRequest, StaticURLs } from "@s-i-s-i/see-learning.shared";
import { Main } from "../../../Components";
import { AuthenticationContext } from "../../../Contexts";
import { jwtIssuer } from "../../../settings";
import { homePageInfo } from "../../Home/pageinfo";
import { onboardingPageInfo } from "../../Onboarding/pageinfo";
import { recoverPageInfo } from "../Recover/pageinfo";
import { setPasswordPageInfo } from "./pageinfo";
import styles from "../authentication.module.css";

export function SetPasswordPage() {
	useActivePageRegistration(setPasswordPageInfo);

	const { email, token, tokenErr, claims, submissionErr, onSubmissionError } = Authentication.useSetPasswordToken({
		expectedTokenIssuer: jwtIssuer,
	});
	const isNewUser = claims?.isNewUser ?? false;

	const { push: pushHistory } = useHistory();
	const onAuthReady = React.useCallback(() => {
		pushHistory(isNewUser ? onboardingPageInfo.to : "/");
	}, [pushHistory, isNewUser]);

	const { status } = React.useContext(AuthenticationContext);
	if (status === Authentication.AuthenticationStatus.Authenticated) {
		return <Redirect to={homePageInfo.to} />;
	}

	return (
		<Main className={styles["auth-page__main"]}>
			<Column
				justify="spaced-center"
				align="center"
				verticalSpacing="1.5rem"
				className={styles["auth-page__content-container"]}
			>
				<Image src={StaticURLs.Images.seeLearningLogo} sizing="width-scaled" style={{ width: "8rem" }} />

				<Heading.H1 noMargin>{isNewUser ? "Welcome" : "Set Password"}</Heading.H1>
				<div style={{ maxWidth: "100%" }}>
					<Conditional>
						<If condition={tokenErr !== null || submissionErr !== null}>
							<Paragraph style={{ textAlign: "center" }}>{tokenErr ?? submissionErr}</Paragraph>

							<Row justify="center">
								<InternalLink
									to={{ pathname: recoverPageInfo.to, state: { formValues: { email } } }}
									className="link--primary link--no-text-decoration"
									style={{ textAlign: "center" }}
								>
									Click here to request a new link
								</InternalLink>
							</Row>
						</If>

						<Else>
							<Paragraph style={{ marginBottom: "1.5rem" }}>
								Set password for: <strong>{email}</strong>
							</Paragraph>

							<SetPasswordForm
								email={email}
								token={token?.raw ?? ""}
								onSubmissionError={onSubmissionError}
								onAuthReady={onAuthReady}
							/>
						</Else>
					</Conditional>
				</div>
			</Column>
		</Main>
	);
}

type SetPasswordFormProps = {
	email: string;
	token: string;
	onSubmissionError: () => void;
	onAuthReady: () => void;
};

function SetPasswordForm({ token, onSubmissionError, onAuthReady }: SetPasswordFormProps) {
	const requestSetPassword = React.useCallback(
		(formValues: Authentication.SetPasswordFormValues) => {
			return serverRequest("/auth/set-password", {
				method: "POST",
				headers: {
					Authorization: `Bearer ${token}`,
				},
				body: JSON.stringify(formValues),
			});
		},
		[token],
	);

	const { formState, onSubmit } = Authentication.useSetPassword({
		AuthenticationContext,
		requestSetPassword,
		onSubmissionError,
		onAuthReady,
	});

	return (
		<Form formState={formState} onSubmit={formState.onSubmitWrapper(onSubmit)} noNotifications noNavigationPrompt>
			<Column justify="spaced-start" verticalSpacing="1.5rem">
				<PasswordField
					formState={formState}
					name="newPassword"
					minLength={8}
					autoFocus
					allowVisibility
					withStrengthEstimator
					maxHashInputLength={72}
					style={{ maxWidth: "100%" }}
				/>

				<SubmitButton
					submissionStatus={formState.submissionStatus}
					onClick={formState.onSubmitWrapper(onSubmit)}
					style={{ width: "100%", fontSize: "1rem", fontWeight: "bold" }}
				>
					Set Password
				</SubmitButton>
			</Column>
		</Form>
	);
}
